import { QueryClient } from '@tanstack/react-query'

import { Group } from '@/models/group'
import {
  GroupHealth,
  GroupSummary,
  OpsGroup,
  OpsGroupsById,
  OpsGroupsListItem,
  StoreWithDeployStatus,
} from '@/models/operations/types'

import { toOpsGroupBase } from './groupInfraFetch'

export const opsQueryKeys = {
  /** Normalized map: groupId → OpsGroup (stores + summary + loadError). */
  groups: ['ops', 'groups-by-id'] as const,
  groupsMetadata: ['ops', 'groups-metadata'] as const,
  bulkJob: (jobId: string) => ['ops', 'bulk-job', jobId] as const,
  bulkActionSession: ['ops', 'bulk-action-session'] as const,
}

export function computeSummary(stores: StoreWithDeployStatus[]): GroupSummary {
  return stores.reduce<GroupSummary>(
    (acc, store) => {
      acc.total += 1
      if (store.isChurned) acc.churned += 1
      else acc.active += 1

      if (store.isActive) {
        if (store.deployUiState === 'running') acc.running += 1
        else if (store.deployUiState === 'building') acc.building += 1
        else acc.failed += 1
      }

      return acc
    },
    { total: 0, active: 0, churned: 0, running: 0, building: 0, failed: 0 },
  )
}

export function computeHealth(summary: GroupSummary): { health: GroupHealth; healthCount?: number } {
  if (summary.failed > 0) {
    return { health: 'FAILED', healthCount: summary.failed }
  }
  if (summary.building > 0) {
    return { health: 'DEGRADED', healthCount: summary.building }
  }
  return { health: 'HEALTHY' }
}

export function toGroupsListItem(group: {
  id: string
  name: string
  description: string
  summary: GroupSummary
  health: GroupHealth
  healthCount?: number
  last_refreshed_at: string | null
  isRefreshing?: boolean
  loadError?: string | null
}) {
  return {
    id: group.id,
    name: group.name,
    description: group.description,
    summary: group.summary,
    health: group.health,
    healthCount: group.healthCount,
    last_refreshed_at: group.last_refreshed_at,
    isRefreshing: group.isRefreshing ?? false,
    loadError: group.loadError ?? null,
  }
}

export function getGroupsById(queryClient: QueryClient): OpsGroupsById {
  return queryClient.getQueryData<OpsGroupsById>(opsQueryKeys.groups) ?? {}
}

export function getGroup(queryClient: QueryClient, groupId: string): OpsGroup | undefined {
  return getGroupsById(queryClient)[groupId]
}

export function getGroupsList(queryClient: QueryClient): OpsGroupsListItem[] {
  const byId = getGroupsById(queryClient)
  const metadata = queryClient.getQueryData<Group[]>(opsQueryKeys.groupsMetadata)
  const ids = metadata?.length ? metadata.map((g) => g.id) : Object.keys(byId)

  return ids
    .map((id) => byId[id])
    .filter((g): g is OpsGroup => g !== undefined)
    .map(toGroupsListItem)
}

export function setGroupsById(queryClient: QueryClient, next: OpsGroupsById): void {
  queryClient.setQueryData(opsQueryKeys.groups, next)
}

export function setGroup(queryClient: QueryClient, groupId: string, group: OpsGroup): void {
  queryClient.setQueryData<OpsGroupsById>(opsQueryKeys.groups, (prev) => ({
    ...(prev ?? {}),
    [groupId]: group,
  }))
}

export function updateGroup(
  queryClient: QueryClient,
  groupId: string,
  updater: (current: OpsGroup | undefined) => OpsGroup,
): void {
  queryClient.setQueryData<OpsGroupsById>(opsQueryKeys.groups, (prev) => {
    const map = prev ?? {}
    return { ...map, [groupId]: updater(map[groupId]) }
  })
}

export function createPlaceholderGroup(group: Group, existing?: OpsGroup): OpsGroup {
  return {
    ...toOpsGroupBase(group),
    summary: existing?.summary ?? {
      total: 0,
      active: 0,
      churned: 0,
      running: 0,
      building: 0,
      failed: 0,
    },
    health: existing?.health ?? 'HEALTHY',
    healthCount: existing?.healthCount,
    last_refreshed_at: existing?.last_refreshed_at ?? null,
    stores: existing?.stores ?? [],
    isRefreshing: true,
    loadError: null,
  }
}

export function seedAllGroupsAsRefreshing(
  queryClient: QueryClient,
  groupsMetadata: Group[],
): OpsGroupsListItem[] {
  const prev = getGroupsById(queryClient)
  const next: OpsGroupsById = {}

  for (const groupMeta of groupsMetadata) {
    next[groupMeta.id] = createPlaceholderGroup(groupMeta, prev[groupMeta.id])
  }

  setGroupsById(queryClient, next)
  return getGroupsList(queryClient)
}

export function patchStoreInGroupCache(
  queryClient: QueryClient,
  groupId: string,
  updatedStore: StoreWithDeployStatus,
): void {
  const group = getGroup(queryClient, groupId)
  if (!group) return

  const stores = group.stores.map((store) =>
    store.id === updatedStore.id ? updatedStore : store,
  )
  const summary = computeSummary(stores)
  const { health, healthCount } = computeHealth(summary)

  setGroup(queryClient, groupId, {
    ...group,
    stores,
    summary,
    health,
    healthCount,
  })
}

export function setOptimisticStoreBuilding(
  queryClient: QueryClient,
  groupId: string,
  storeId: string,
): void {
  const group = getGroup(queryClient, groupId)
  const store = group?.stores.find((s) => s.id === storeId)
  if (!store) return

  patchStoreInGroupCache(queryClient, groupId, {
    ...store,
    deployment_status: 'BUILDING',
    deployUiState: 'building',
  })
}

export function setOptimisticActiveStoresBuilding(queryClient: QueryClient, groupId: string): void {
  const group = getGroup(queryClient, groupId)
  if (!group) return

  const stores = group.stores.map((store) =>
    store.isActive
      ? { ...store, deployment_status: 'BUILDING', deployUiState: 'building' as const }
      : store,
  )
  const summary = computeSummary(stores)
  const { health, healthCount } = computeHealth(summary)

  setGroup(queryClient, groupId, {
    ...group,
    stores,
    summary,
    health,
    healthCount,
  })
}
